import React, { useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLocationParams } from '../../hooks/useLocationParams';
import { PageBanner } from '../../components/PageBanner/PageBanner';
import { PageNavigation, Tab } from '../../components/PageNavigation/PageNavigation';
import { MultiStepForm, Step } from '../../components/MultiStepForm/MultiStepForm';
import { UserInfoStep } from '../../components/MultiStepForm/steps/UserInfoStep';
import { DateTimeStep } from '../../components/MultiStepForm/steps/DateTimeStep';
import { ConfirmationStep } from '../../components/MultiStepForm/steps/ConfirmationStep';
import { AdditionalInfoStep } from '../../components/MultiStepForm/steps/AdditionalInfoStep';
import { CompletedStep } from '../../components/MultiStepForm/steps/CompletedStep';
import { BookingConfirmation } from '../../components/BookingConfirmation/BookingConfirmation';
import { Toast } from '../../components/Toast/Toast';
import formIconCeu from '../../assets/icons/form-icon-ceu.svg';
import formIconCalendar from '../../assets/icons/form-icon-calendar.svg';
import formIconInfo from '../../assets/icons/form-icon-info.svg';
import formIconConfirmNotReached from '../../assets/icons/form-icon-confirm-not-reached.svg';
import formIconConfirmReached from '../../assets/icons/form-icon-confirm-reached.svg';
import formIconNotReached from '../../assets/icons/form-icon-step-not-reached-yet.svg';
import { CityFilter } from '../../components/CityFilter/CityFilter';
import ShowroomTabs, { ShowroomTab } from '../../components/ShowroomTabs/ShowroomTabs';
import { useAppointments } from '../../hooks/useAppointments';
import { usePrivateAppointments } from '../../hooks/usePrivateAppointments';
import { useAppointmentBooking } from '../../hooks/useAppointmentBooking';
import { adaptAppointmentToCardEvent } from '../../adapters/appointmentAdapter';
import './AppointmentPage.scss';
import { EventCard } from '@/components/EventCard/EventCard';
import languageIcon from '../../assets/icons/language-icon-white.svg';

// Feature flag: Set to true to show appointments, false to show "Coming Soon"
const SHOW_APPOINTMENTS = import.meta.env.VITE_MICROSITE_SHOW_APPOINTMENTS === "true";

// Deduplication function to handle duplicate appointments from API
const deduplicateAppointments = (appointments: any[] | undefined): any[] => {
  if (!appointments) return [];
  
  return appointments.reduce((acc, appointment) => {
    // Create unique key: appointmentId + appointmentType + territoryId
    const uniqueKey = `${appointment.appointmentId}-${appointment.appointmentType}-${appointment.location.territoryId}`;
    
    if (!acc.some((a: any) => 
      `${a.appointmentId}-${a.appointmentType}-${a.location.territoryId}` === uniqueKey
    )) {
      acc.push(appointment);
    }
    return acc;
  }, [] as any[]);
};

const AppointmentPage: React.FC = () => {
  const { t, i18n } = useTranslation('appointments');
  const navigate = useNavigate();
  const { location: initialLocation, privateAppointmentParams, testingParams, updateParams } = useLocationParams();
  const [selectedCity, setSelectedCity] = useState(initialLocation);
  
  // Only fetch appointments if the feature is enabled OR if in testing mode
  // Use private appointments endpoint if in private mode, otherwise use public
  const { data: rawAppointments, isLoading: isAppointmentsLoading } = (SHOW_APPOINTMENTS || testingParams.isTesting)
    ? (privateAppointmentParams.isPrivate 
        ? usePrivateAppointments(selectedCity) 
        : useAppointments(selectedCity))
    : { data: [], isLoading: false };
  const appointments = deduplicateAppointments(rawAppointments);
  const { bookAppointment, isPending: isSubmitting } = useAppointmentBooking();
  const [activeTab, setActiveTab] = useState('booking');

  const [selectedShowroom, setSelectedShowroom] = useState<ShowroomTab>('all');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    // Initialize with private appointment params if available
    if (privateAppointmentParams.isPrivate) {
      return {
        firstName: privateAppointmentParams.firstName || '',
        lastName: privateAppointmentParams.lastName || '',
        email: privateAppointmentParams.email || '',
        phone: privateAppointmentParams.phone || '',
      };
    }
    return {};
  });

  // useEffect hooks after all state declarations
  if (isAppointmentsLoading) {
    return <Loader variant="fullscreen" />;
  }

  const handleStepComplete = (stepData: Record<string, any>) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const eventsTabs: Tab[] = privateAppointmentParams.isPrivate
    ? [{ id: 'booking', label: t('navigation.booking') }]
    : [
        { id: 'events', label: t('navigation.events') },
        { id: 'booking', label: t('navigation.booking') }
      ];

  const handleTabChange = (tabId: string) => {
    if (tabId === 'events') {
      navigate('/events');
    } else {
      setActiveTab(tabId);
    }
  };

  const handleCloseForm = () => {
    setShowBookingForm(false);
  };

  const handleFormComplete = async (formData: Record<string, any>) => {
    try {
      // Adapt the appointment to get formType
      const adaptedAppointment = selectedAppointment ? adaptAppointmentToCardEvent(selectedAppointment) : null;
      
      // Get workTypeGroupId from form data or fallback to selected appointment
      // Make sure we have a string value for workTypeGroupId
      const workTypeGroupId = (formData.workTypeGroupId || selectedAppointment?.workTypeGroupId || '').toString();
      
      // Make sure we have a string value for appointmentId
      const appointmentId = (formData.appointmentId || workTypeGroupId || '').toString();
      
      // Create booking data object that matches AppointmentBookingRequest type
      const bookingData = {
        // Required fields from AppointmentBookingRequest
        appointmentId: appointmentId,
        workTypeGroupId: workTypeGroupId,
        startTime: formData.startTime,
        endTime: formData.endTime,
        // Use territoryId from formData or fallback
        serviceTerritoryId: formData.territoryId || '',
        // Use resourceId from formData and convert to array
        resourceIds: formData.resourceId ? [formData.resourceId] : [],
        // Map firstName/lastName to firstname/lastname (case difference in API)
        firstname: formData.firstName,
        lastname: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company || '',
        
        // Optional fields
        additionalNotes: formData.additionalNotes || '',
        language: i18n.language === 'fr' ? 'French' : 'English',
        experienceCenter: selectedAppointment?.city === 'Montreal' ? 'Montreal' : 'Toronto',
        appointmentType: adaptedAppointment?.formType || '',
        
        // Address fields
        addressStreet: formData.streetAddress || '',
        addressPostalCode: formData.postalCode || '',
        addressCity: formData.city || selectedAppointment?.city || '',
        addressState: formData.province || (selectedAppointment?.city === 'Montreal' ? 'Quebec' : 'Ontario'),
        addressCountry: formData.addressCountry || 'Canada'
      };
      
      // Add appointment type specific fields if needed
      const appointmentType = adaptedAppointment?.formType || '';
      console.log('Appointment type:', appointmentType, 'Adapted appointment:', adaptedAppointment);
      
      if (appointmentType === 'boardroom-meeting') {
        Object.assign(bookingData, {
          meetingDetails: formData.meetingDetails || '',
          audioVideoRequirements: formData.audioVideoRequirements || '',
          cateringRequirements: formData.cateringRequirements || '',
          tradeProfessionals: formData.tradeProfessionals || '',
          additionalComments: formData.additionalComments || ''
        });
      } else if (appointmentType === 'product-consultation') {
        // Product consultation specific fields
        Object.assign(bookingData, {
          visitedShowroom: formData.visitedShowroom === 'yes' ? 'Yes' : 'No',
          productsInterest: formData.productsInterested || '',
          applianceRetailer: formData.retailer || '',
          tradeProfessionals: formData.tradeProfessionals || '',
          additionalComments: formData.additionalComments || ''
        });
      } else if (appointmentType === 'private-ownership') {
        // Private ownership specific fields
        Object.assign(bookingData, {
          foodDietaryRestriction: formData.foodAllergies || '',
          guestName: formData.guestName || '',
          productsRelevant: formData.productsOwned || '',
          additionalComments: formData.additionalComments || ''
        });
      } else if (appointmentType === 'private-intro-class') {
        // Private intro class specific fields
        Object.assign(bookingData, {
          foodDietaryRestriction: formData.foodAllergies || '',
          guestName: formData.guestName || '',
          productsInterest: formData.productsInterested || '',
          applianceRetailer: formData.retailer || '',
          tradeProfessionals: formData.tradeProfessionals || '',
          additionalComments: formData.additionalComments || ''
        });
      } else if (appointmentType === 'builder-package-viewing') {
        // Builder package viewing specific fields
        Object.assign(bookingData, {
          developer: formData.developer || '',
          siteName: formData.siteName || '',
          lotSuiteNumber: formData.lotSuiteNumber || '',
          productsInterest: formData.productsInterested || '',
          additionalComments: formData.additionalComments || ''
        });
      } else {
        // Common fields for other appointment types
        Object.assign(bookingData, {
          foodDietaryRestriction: formData.foodAllergies || '',
          guestName: formData.guestName || '',
          productsInterest: formData.productsInterested || '',
          applianceRetailer: formData.retailer || '',
          tradeProfessionals: formData.tradeProfessionals || '',
          additionalComments: formData.additionalComments || ''
        });
      }
      
      // Add contactId if this is a private appointment
      const finalBookingData = privateAppointmentParams.isPrivate && privateAppointmentParams.contactId
        ? { ...bookingData, contactId: privateAppointmentParams.contactId }
        : bookingData;
            
      // Submit booking to API - same endpoint for both public and private
      await bookAppointment(finalBookingData, {
        onSuccess: () => {
          setShowBookingForm(false);
          setShowConfirmation(true);
        },
        onError: (error: any) => {
          console.error('Error booking appointment:', error);
          const errorMsg = error?.response?.data?.message 
            || error?.message 
            || t('form.errors.bookingFailed');
          setErrorMessage(errorMsg);
          // Close the modal so user can see the error toast clearly
          setShowBookingForm(false);
        }
      });
    } catch (error: any) {
      console.error('Error booking appointment:', error);
      const errorMsg = error?.response?.data?.message 
        || error?.message 
        || t('form.errors.bookingFailed');
      setErrorMessage(errorMsg);
      // Close the modal so user can see the error toast clearly
      setShowBookingForm(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Helper to determine if additional info step should be shown
  const shouldShowAdditionalInfo = (formType: string | undefined): boolean => {
    if (!formType) return false;
    // For private appointments, only show additional info for builder-package-viewing
    if (privateAppointmentParams.isPrivate) {
      return formType === 'builder-package-viewing';
    }
    // For public appointments, exclude event types, special-event, and unknown-appointment
    return !formType.startsWith('event-') && formType !== 'special-event' && formType !== 'unknown-appointment';
  };

  // Helper to get appointment title with fallback to original name if translation doesn't exist
  const getAppointmentTitle = (appointmentName: string | undefined): string => {
    if (!appointmentName) return '';
    
    // Convert name to camelCase for translation key
    const translationKey = `appointmentTitles.${appointmentName.split(' ').map((word: string, index: number) => 
      index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)
    ).join('')}`;
    
    // Try to get translation
    const translated = t(translationKey);
    
    // If translation key is returned as-is (not found), use the original name
    if (translated === translationKey || translated.startsWith('appointmentTitles.')) {
      return appointmentName.toUpperCase();
    }
    
    return translated.toUpperCase();
  };

  const bookingSteps: Step[] = [
    {
      id: 'event',
      icon: { src: formIconCeu, alt: 'Event Icon' },
      title: getAppointmentTitle(selectedAppointment?.name),
      Component: <CompletedStep onNext={() => {}} />
    },
    {
      id: 'datetime',
      icon: { src: formIconCalendar, alt: 'Calendar Icon' },
      title: t('form.steps.dateTime'),
      Component: <DateTimeStep 
        onNext={handleStepComplete} 
        initialData={formData} 
        appointmentId={selectedAppointment?.workTypeGroupId || selectedAppointment?.appointmentId}
        showroom={selectedAppointment?.showroom}
        location={selectedCity}
      />
    },
    {
      id: 'info',
      icon: { src: (isReached: boolean) => isReached ? formIconInfo : formIconNotReached, alt: 'Information Icon' },
      title: t('form.steps.yourInformation'),
      Component: <UserInfoStep
        onNext={handleStepComplete}
        initialData={formData}
        formType={(selectedAppointment && adaptAppointmentToCardEvent(selectedAppointment).formType) || 'product-consultation'}
      />
    },
    // Conditionally include Additional Info step only if formType requires it
    ...(shouldShowAdditionalInfo(selectedAppointment ? adaptAppointmentToCardEvent(selectedAppointment).formType : 'product-consultation') ? [{
      id: 'additionalInfo',
      icon: { src: (isReached: boolean) => isReached ? formIconInfo : formIconNotReached, alt: 'Additional Info Icon' },
      title: t('form.steps.additionalInfo'),
      Component: <AdditionalInfoStep 
        onNext={handleStepComplete} 
        initialData={formData} 
        formType={(selectedAppointment && adaptAppointmentToCardEvent(selectedAppointment).formType) || 'product-consultation'}
        isPrivateAppointment={privateAppointmentParams.isPrivate}
      />
    }] : []),
    {
      id: 'confirm',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconConfirmReached : formIconConfirmNotReached,
        alt: 'Confirmation Icon'
      },
      title: t('form.steps.reviewAndConfirm'),
      Component: <ConfirmationStep 
        eventData={selectedAppointment ? adaptAppointmentToCardEvent(selectedAppointment) : {
          id: '',
          remoteObjectId: '',
          title: '',
          type: 'In-Person',
          duration: '',
          description: '',
          date: '',
          time: '',
          location: '',
          city: 'Montreal',
          showroom: 'Montreal Live Kitchen',
          imageUrl: '',
          imageEventType: 'product-consultation'
        }} 
        initialData={formData} 
        onNext={handleStepComplete} 
        variant="appointment"
        isPrivateAppointment={privateAppointmentParams.isPrivate}
      />
    }
  ];

  return (
    <div className="appointment-page">
      <button 
        className="language-toggle"
        onClick={() => {
          const newLang = i18n.language === 'en' ? 'fr' : 'en';
          i18n.changeLanguage(newLang);
          updateParams({ lang: newLang });
        }}
      >
        <img src={languageIcon} alt="Change Language" />
        <span>{i18n.language.toUpperCase()}</span>
      </button>
      <PageBanner
        title={t('banner.title')}
        subtitle={t('banner.subtitle')}
        className="appointment-page__banner"
      />
      <div className="appointment-page__content">
        <PageNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={eventsTabs}
        />
        <div className="appointment-page__inner-content">
          {(SHOW_APPOINTMENTS || testingParams.isTesting) ? (
            <div className="appointment-page__appointments-content">
              <div className="filters-container">
                <CityFilter
                  cities={['Toronto', 'Montreal']}
                  selectedCity={selectedCity}
                  onCityChange={(city) => {
                    setSelectedCity(city);
                    setSelectedShowroom('all');
                    updateParams({ location: city });
                  }}
                />
                {selectedCity === 'Montreal' && (
                  <ShowroomTabs
                    selectedTab={selectedShowroom}
                    onTabChange={setSelectedShowroom}
                    tabs={[
                      { id: 'all', label: t('showroom.tabs.all'), count: appointments?.filter(a => a.city === selectedCity).length || 0 },
                      { 
                        id: 'Montreal Showroom', 
                        label: t('showroom.montreal'), 
                        count: appointments?.filter(a => {
                          const showroomUpper = a.showroom?.toUpperCase() || '';
                          return a.city === selectedCity && showroomUpper.includes('MONTREAL') && !showroomUpper.includes('ROYALMOUNT');
                        }).length || 0 
                      },
                      { 
                        id: 'RoyalMount Showroom', 
                        label: t('showroom.royalmount'), 
                        count: appointments?.filter(a => {
                          const showroomUpper = a.showroom?.toUpperCase() || '';
                          return a.city === selectedCity && showroomUpper.includes('ROYALMOUNT');
                        }).length || 0 
                      }
                    ]}
                  />
                )}
              </div>
              <div className="appointment-page__appointments-grid">
                {appointments
                  ?.filter(appointment => {
                    // First filter by city
                    if (appointment.city !== selectedCity) return false;
                    
                    // Then filter by showroom if needed
                    if (selectedShowroom === 'all') return true;
                    
                    const showroomUpper = appointment.showroom?.toUpperCase() || '';
                    
                    if (selectedShowroom === 'RoyalMount Showroom') {
                      return showroomUpper.includes('ROYALMOUNT');
                    } else if (selectedShowroom === 'Montreal Showroom') {
                      return showroomUpper.includes('MONTREAL') && !showroomUpper.includes('ROYALMOUNT');
                    }
                    return false;
                  })
                  .map((appointment) => (
                    <EventCard
                      key={`${appointment.appointmentId}-${appointment.appointmentType}-${appointment.location.territoryId}`}
                      event={adaptAppointmentToCardEvent(appointment)}
                      formVariant="appointment"
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setShowBookingForm(true);
                      }}
                    />
                  ))}
              </div>
            </div>
          ) : (
            <h1>{t('comingSoon')}</h1>
          )}
        </div>
      </div>

      {showBookingForm && (
        <MultiStepForm
          steps={bookingSteps}
          initialStep={0}
          initialFormData={formData}
          onClose={handleCloseForm}
          onComplete={handleFormComplete}
          variant="appointment"
          isSubmitting={isSubmitting}
        />
      )}

      {showConfirmation && (
        <BookingConfirmation onClose={handleCloseConfirmation} />
      )}

      {errorMessage && (
        <Toast 
          message={errorMessage} 
          type="error" 
          onClose={() => setErrorMessage(null)} 
        />
      )}
    </div>
  );
};

export default AppointmentPage;
